/* eslint-disable  */
import React from 'react';
import { motion } from 'framer-motion';
import { Button, Typography } from 'antd';
import { Sparkles } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import { setCurrentPage } from '@/store/account/slice';

const { Title, Text } = Typography;

interface TrialOverlayProps {
	show: boolean;
	title?: string;
	description?: string;
	buttonText?: string;
}

const TrialOverlay: React.FC<TrialOverlayProps> = ({
	show,
	title = 'Unlock Full Features',
	description = 'Start your free trial today to unlock all premium features and experience the full potential of our platform.',
	buttonText = 'Start 7-Day Free Trial',
}) => {
	if (!show) {
		return null;
	}
	const dispatch = useDispatch();

	const handleStartTrial = () => {
		dispatch(setCurrentPage(ReceptionistDashboardSiderItem.GET_STARTED));
	};

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			className="trial-overlay backdrop-blur-md bg-white/50 flex items-center justify-center"
		>
			{/* Background gradient effect */}
			<motion.div
				className="absolute inset-0 bg-gradient-to-r from-blue-400/10 via-purple-400/10 to-pink-400/10 opacity-70"
				animate={{
					background: [
						'linear-gradient(to right, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1), rgba(236, 72, 153, 0.1))',
						'linear-gradient(to right, rgba(236, 72, 153, 0.1), rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1))',
						'linear-gradient(to right, rgba(147, 51, 234, 0.1), rgba(236, 72, 153, 0.1), rgba(59, 130, 246, 0.1))',
					],
				}}
				transition={{ duration: 10, repeat: Infinity }}
			/>

			{/* Animated particles for a premium feel */}
			<div className="absolute inset-0 overflow-hidden">
				{[...Array(20)].map((_, i) => (
					<motion.div
						key={i}
						className="absolute rounded-full bg-white"
						style={{
							width: Math.random() * 6 + 2,
							height: Math.random() * 6 + 2,
							left: `${Math.random() * 100}%`,
							top: `${Math.random() * 100}%`,
						}}
						animate={{
							y: [0, -30, 0],
							opacity: [0, 1, 0],
						}}
						transition={{
							duration: Math.random() * 3 + 2,
							repeat: Infinity,
							delay: Math.random() * 5,
						}}
					/>
				))}
			</div>

			<div className="relative z-10 p-8 max-w-lg mx-auto text-center">
				<motion.div
					initial={{ scale: 0.9, opacity: 0 }}
					animate={{ scale: 1, opacity: 1 }}
					transition={{ delay: 0.1, duration: 0.5 }}
					className="space-y-8"
				>
					<div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-600 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
						<Sparkles className="w-10 h-10 text-white" />
					</div>

					<div className="space-y-4">
						<Title level={3} className="!text-gray-800 !m-0">
							{title}
						</Title>
						<Text className="text-gray-600 block text-lg">{description}</Text>
					</div>

					<motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
						<Button
							type="primary"
							size="large"
							onClick={handleStartTrial}
							className="h-12 px-8 text-lg bg-gradient-to-r from-blue-500 to-purple-600 border-0 shadow-md"
						>
							{buttonText}
						</Button>
					</motion.div>
				</motion.div>
			</div>
		</motion.div>
	);
};

export default TrialOverlay;
